import { useState } from "react";
import { Crown, Lock, Mail, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { checkVipAccess, createVipCheckoutSession } from "@/lib/api";
import { pickText } from "@/lib/content";

export const VipAccessGate = ({ language, vipEmail, onAccessResolved }) => {
  const [email, setEmail] = useState(vipEmail || "");
  const [busy, setBusy] = useState(false);

  const handleCheckout = async () => {
    if (!email) {
      toast.error(pickText(language, "Enter your email first.", "Inserisci prima la tua email."));
      return;
    }

    try {
      setBusy(true);
      const data = await createVipCheckoutSession({ email, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to open Stripe checkout.", "Impossibile aprire il checkout Stripe."));
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!email) {
      toast.error(pickText(language, "Enter your purchase email first.", "Inserisci prima l'email di acquisto."));
      return;
    }

    try {
      setBusy(true);
      const data = await checkVipAccess({ email });
      if (data.access_granted) {
        onAccessResolved(email, data);
        toast.success(pickText(language, "VIP access confirmed.", "Accesso VIP confermato."));
        return;
      }
      toast.error(pickText(language, "No active VIP access found for this email yet.", "Nessun accesso VIP attivo trovato ancora per questa email."));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to verify access right now.", "Impossibile verificare l'accesso in questo momento."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_0.9fr]" data-testid="vip-access-gate">
      <div className="glass-card rounded-[2rem] p-6 md:p-8">
        <p className="section-label" data-testid="vip-gate-eyebrow">
          {pickText(language, "V.I.P. Access Gate", "Porta di Accesso V.I.P.")}
        </p>
        <h2 className="mt-4 text-4xl text-foreground md:text-5xl" data-testid="vip-gate-title">
          {pickText(language, "Unlock the private chambers before entering the app.", "Sblocca le camere private prima di entrare nell'app.")}
        </h2>
        <p className="body-copy mt-4" data-testid="vip-gate-description">
          {pickText(
            language,
            "Annual V.I.P. access is $33 and is linked to your email. Stripe handles checkout and sends the purchase receipt automatically.",
            "L'accesso annuale V.I.P. costa $33 ed è collegato alla tua email. Stripe gestisce il checkout e invia automaticamente la ricevuta di acquisto.",
          )}
        </p>
        <div className="mt-6 space-y-3">
          <div className="flex items-center gap-3 rounded-3xl border border-white/10 bg-black/20 px-4 py-4" data-testid="vip-gate-price-card">
            <Crown className="h-5 w-5 text-primary" />
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-primary/80">VIP Annual</p>
              <p className="text-3xl text-foreground">$33</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-3xl border border-white/10 bg-black/20 px-4 py-4" data-testid="vip-gate-feature-card">
            <Sparkles className="h-5 w-5 text-primary" />
            <p className="text-sm text-slate-300">
              {pickText(language, "Includes the 3D-feeling image-aware oracle and all locked routes.", "Include l'oracolo con sensazione 3D consapevole delle immagini e tutte le rotte bloccate.")}
            </p>
          </div>
        </div>
      </div>

      <div className="glass-card rounded-[2rem] p-6 md:p-8">
        <div className="flex items-center gap-3 text-primary" data-testid="vip-gate-form-header">
          <Lock className="h-5 w-5" />
          <p className="section-label">{pickText(language, "Enter with email", "Entra con email")}</p>
        </div>
        <div className="mt-6 space-y-4">
          <div className="relative">
            <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              className="h-12 rounded-full border-white/10 bg-black/20 pl-11 text-white"
              data-testid="vip-gate-email-input"
              onChange={(event) => setEmail(event.target.value)}
              placeholder={pickText(language, "your@email.com", "tuo@email.com")}
              type="email"
              value={email}
            />
          </div>
          <Button className="ritual-button h-auto w-full justify-center rounded-full" data-testid="vip-gate-checkout-button" disabled={busy} onClick={handleCheckout} type="button">
            {pickText(language, "Subscribe for $33 / year", "Abbonati per $33 / anno")}
          </Button>
          <Button className="ghost-button h-auto w-full justify-center rounded-full bg-transparent" data-testid="vip-gate-verify-button" disabled={busy} onClick={handleVerify} type="button" variant="ghost">
            {pickText(language, "Already paid? Verify email", "Hai già pagato? Verifica email")}
          </Button>
        </div>
      </div>
    </div>
  );
};